import { getApiBaseUrl } from "../utils/getApiBaseUrl";

const SITE_KEY_COM = "treydoraCom";
const API_BASE = getApiBaseUrl();
const HEARTBEAT_MS = 60_000;
const IDLE_MS = 5 * 60 * 1000;
const DEDUPE_MS = 500;

let sessionId = null;
let anonymousId = null;
let heartbeatTimer = null;
let lastActivityAt = Date.now();
let lastEventAt = 0;
let isVisible = typeof document !== "undefined" ? document.visibilityState === "visible" : true;
let isFocused = typeof document !== "undefined" ? document.hasFocus?.() ?? true : true;
let pendingEvents = [];
let flushTimer = null;

function isAdminUser(user) {
  const slug = String(user?.role?.slug || user?.userType || "").toLowerCase();
  return slug === "admin";
}

function telemetryEnabled() {
  if (process.env.REACT_APP_DISABLE_PRODUCT_TELEMETRY === "true") return false;
  if (process.env.NODE_ENV === "development" && process.env.REACT_APP_ENABLE_TELEMETRY_IN_DEV !== "true") {
    return false;
  }
  try {
    const raw = localStorage.getItem("currentUser");
    if (raw) {
      const user = JSON.parse(raw);
      if (isAdminUser(user)) return false;
    }
  } catch {
    /* ignore */
  }
  return true;
}

function getAnonymousId() {
  if (anonymousId) return anonymousId;
  try {
    const key = "td_anonymous_id_com";
    anonymousId = localStorage.getItem(key);
    if (!anonymousId) {
      anonymousId = crypto.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(key, anonymousId);
    }
  } catch {
    anonymousId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
  return anonymousId;
}

function getSessionId() {
  if (sessionId) return sessionId;
  try {
    const key = "td_analytics_session_com";
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.sessionId) {
        sessionId = parsed.sessionId;
        return sessionId;
      }
    }
    sessionId = crypto.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(key, JSON.stringify({ sessionId, startedAt: Date.now() }));
  } catch {
    sessionId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
  return sessionId;
}

function isIdle() {
  return Date.now() - lastActivityAt > IDLE_MS;
}

function canAccumulateActiveTime() {
  return isVisible && isFocused && !isIdle();
}

async function postJson(path, body, useBeacon = false) {
  const payload = JSON.stringify(body);
  if (useBeacon && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    navigator.sendBeacon(`${API_BASE}${path}`, blob);
    return;
  }
  await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: payload,
    credentials: "include",
  });
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEvents().catch(() => {});
  }, 1500);
}

async function flushEvents(useBeacon = false) {
  if (!telemetryEnabled() || !pendingEvents.length) return;
  const events = pendingEvents.splice(0, 50);
  await postJson(
    "/api/telemetry/events",
    {
      siteKey: SITE_KEY_COM,
      sessionId: getSessionId(),
      anonymousId: getAnonymousId(),
      events,
    },
    useBeacon
  );
}

export async function startSession(metadata = {}) {
  if (!telemetryEnabled()) return null;
  getSessionId();
  try {
    await postJson("/api/telemetry/sessions/start", {
      siteKey: SITE_KEY_COM,
      sessionId: getSessionId(),
      anonymousId: getAnonymousId(),
      ...metadata,
    });
  } catch {
    /* failure-safe */
  }
  startHeartbeat();
  return getSessionId();
}

function startHeartbeat() {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(() => {
    if (!canAccumulateActiveTime()) return;
    postJson("/api/telemetry/sessions/heartbeat", {
      siteKey: SITE_KEY_COM,
      sessionId: getSessionId(),
    }).catch(() => {});
  }, HEARTBEAT_MS);
}

export function markActivity() {
  lastActivityAt = Date.now();
}

export function trackEvent(name, payload = {}, opts = {}) {
  if (!telemetryEnabled()) return;
  const now = Date.now();
  if (!opts.skipDedupe && now - lastEventAt < DEDUPE_MS && !opts.force) return;
  lastEventAt = now;
  markActivity();
  pendingEvents.push({
    eventName: name,
    eventVersion: 1,
    siteKey: SITE_KEY_COM,
    sessionId: getSessionId(),
    anonymousId: getAnonymousId(),
    occurredAt: new Date().toISOString(),
    path: typeof window !== "undefined" ? window.location.pathname : "",
    ...payload,
  });
  scheduleFlush();
}

export function trackAreaView(area, metadata = {}) {
  trackEvent("area_view", { area, metadata, path: window.location.pathname });
}

export function trackProductViewed(entityType, entityId, metadata = {}) {
  trackEvent("product_viewed", { area: "product", entityType, entityId, metadata });
}

export function trackCheckoutStarted(metadata = {}) {
  trackEvent("checkout_started", { area: "checkout", metadata });
}

export function trackWalletConnectionStarted(metadata = {}) {
  trackEvent("wallet_connection_started", { area: "wallet", metadata });
}

export function trackWalletConnected(metadata = {}) {
  trackEvent("wallet_connected", { area: "wallet", metadata });
}

export function trackWalletConnectionFailed(metadata = {}) {
  trackEvent("wallet_connection_failed", { area: "wallet", metadata });
}

export function trackRegistrationCompleted(metadata = {}) {
  trackEvent("registration_completed", { area: "account", metadata, force: true });
}

export function trackLoginCompleted(metadata = {}) {
  trackEvent("login_completed", { area: "account", metadata, force: true });
}

export async function endSession(metadata = {}) {
  if (!telemetryEnabled()) return;
  await flushEvents(true);
  try {
    await postJson(
      "/api/telemetry/sessions/end",
      { siteKey: SITE_KEY_COM, sessionId: getSessionId(), ...metadata },
      true
    );
  } catch {
    /* ignore */
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

export function areaForPath(pathname = "") {
  const path = String(pathname || "");
  if (path === "/" || path.startsWith("/home")) return "home";
  if (path.includes("/world/oshar/real-estate")) return "real_estate";
  if (path.startsWith("/world/oshar") || path.startsWith("/world")) return "oshar";
  if (path.startsWith("/marketplace")) return "marketplace";
  if (path.startsWith("/library/read")) return "library";
  if (path.startsWith("/library")) return "library";
  if (path.includes("treydorex") || path.includes("/token")) return "trdx";
  if (path.startsWith("/dashboard")) return "game_access";
  if (path.startsWith("/login") || path.startsWith("/signup")) return "account";
  return "home";
}

export function initProductTelemetry() {
  if (!telemetryEnabled() || typeof window === "undefined") return () => {};
  const onActivity = () => markActivity();
  ["pointerdown", "keydown", "scroll", "touchstart"].forEach((ev) =>
    window.addEventListener(ev, onActivity, { passive: true })
  );
  const onVisibility = () => {
    isVisible = document.visibilityState === "visible";
  };
  const onFocus = () => {
    isFocused = true;
  };
  const onBlur = () => {
    isFocused = false;
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  startSession({ entryArea: areaForPath(window.location.pathname) });
  window.addEventListener("beforeunload", () => {
    endSession({ exitArea: areaForPath(window.location.pathname) });
  });
  return () => {
    ["pointerdown", "keydown", "scroll", "touchstart"].forEach((ev) =>
      window.removeEventListener(ev, onActivity)
    );
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("blur", onBlur);
    endSession();
  };
}

export default {
  trackEvent,
  trackAreaView,
  trackProductViewed,
  trackCheckoutStarted,
  trackWalletConnectionStarted,
  trackWalletConnected,
  trackWalletConnectionFailed,
  trackRegistrationCompleted,
  trackLoginCompleted,
  initProductTelemetry,
  areaForPath,
};
